import React, { useState } from 'react';
import { X } from 'lucide-react';
import { IconButton, PrimaryButton, TertiaryButton } from '../../../components/brand/BrandButton';
import { Heading } from '../../../components/brand/BrandTypography';
import { TOOL_TYPE_CONFIG } from '../constants';

const ToolEditorModal = ({ tool, onSave, onClose, isNew = false }) => {
    const [formData, setFormData] = useState({
        tool_id: tool?.tool_id || '',
        type: tool?.type || 'breath',
        name: tool?.name || '',
        instruction: tool?.instruction || '',
        duration: tool?.duration || 10,
        cycles: tool?.cycles || null,
        speed: tool?.speed || null,
        optional: tool?.optional || false,
        variants: tool?.variants || {},
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await onSave(formData, isNew);
            onClose();
        } catch (error) {
            console.error('Error saving tool:', error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-[#FFBB98] p-4 flex items-center justify-between">
                    <Heading as="h2" size="text-lg">
                        {isNew ? 'Create New Tool' : 'Edit Tool'}
                    </Heading>
                    <IconButton onClick={onClose}>
                        <X className="w-5 h-5" />
                    </IconButton>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {isNew && (
                        <div>
                            <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                                Tool ID (snake_case)
                            </label>
                            <input
                                type="text"
                                value={formData.tool_id}
                                onChange={(e) => setFormData({...formData, tool_id: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                                className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] bg-white focus:outline-none focus:ring-2 focus:ring-[#7D8E95]"
                                placeholder="e.g., new_breath_tool"
                                required
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                            Type
                        </label>
                        <select
                            value={formData.type}
                            onChange={(e) => setFormData({...formData, type: e.target.value})}
                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] bg-white focus:outline-none focus:ring-2 focus:ring-[#7D8E95]"
                        >
                            {Object.entries(TOOL_TYPE_CONFIG).map(([key, config]) => (
                                <option key={key} value={key}>{config.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                            Display Name
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] bg-white focus:outline-none focus:ring-2 focus:ring-[#7D8E95]"
                            placeholder="e.g., Chest Breathing"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                            Instruction Text
                        </label>
                        <textarea
                            value={formData.instruction}
                            onChange={(e) => setFormData({...formData, instruction: e.target.value})}
                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] bg-white focus:outline-none focus:ring-2 focus:ring-[#7D8E95] min-h-[100px]"
                            placeholder="The instruction shown to the user..."
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                            Duration (seconds) - 0 for no timer
                        </label>
                        <div className="flex items-center gap-3">
                            <input
                                type="range"
                                min="0"
                                max="30"
                                value={formData.duration}
                                onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
                                className="flex-1"
                            />
                            <span className="w-12 text-center font-mono text-[#1D6B6B]">
                                {formData.duration}s
                            </span>
                        </div>
                    </div>

                    {formData.type === 'visual_bls' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                                    Cycles
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={formData.cycles || 3}
                                    onChange={(e) => setFormData({...formData, cycles: parseInt(e.target.value)})}
                                    className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] bg-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                                    Speed (sec/cycle)
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={formData.speed || 3}
                                    onChange={(e) => setFormData({...formData, speed: parseInt(e.target.value)})}
                                    className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] bg-white"
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="optional"
                            checked={formData.optional}
                            onChange={(e) => setFormData({...formData, optional: e.target.checked})}
                            className="w-4 h-4 rounded border-[#FFBB98]"
                        />
                        <label htmlFor="optional" className="text-sm text-[#1D6B6B]">
                            Optional step (user can skip)
                        </label>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <TertiaryButton type="button" onClick={onClose} className="flex-1">
                            Cancel
                        </TertiaryButton>
                        <PrimaryButton type="submit" disabled={saving} className="flex-1">
                            {saving ? 'Saving...' : (isNew ? 'Create Tool' : 'Save Changes')}
                        </PrimaryButton>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ToolEditorModal;
